import CircularProgress from '@material-ui/core/CircularProgress';
import React from 'react';
import { Box, Button, Dialog } from '@attain-sre/attain-design-system';
import {
  OTS_SGM_MNG_DELETE_RESULTS_CLOSE,
  OTS_SGM_MNG_DELETE_RESULTS_ERROR_EMPTY,
  OTS_SGM_MNG_DELETE_RESULTS_FAILED,
  OTS_SGM_MNG_DELETE_RESULTS_REQUESTED,
  OTS_SGM_MNG_DELETE_RESULTS_STATUS_FAILED,
  OTS_SGM_MNG_DELETE_RESULTS_STATUS_SUCCESS,
  OTS_SGM_MNG_DELETE_RESULTS_SUCCEEDED,
  OTS_SGM_MNG_DELETE_RESULTS_SUMMARY_ALL_OK,
  OTS_SGM_MNG_DELETE_RESULTS_SUMMARY_NONE,
  OTS_SGM_MNG_DELETE_RESULTS_SUMMARY_PARTIAL,
  OTS_SGM_MNG_DELETE_RESULTS_TABLE_ERROR,
  OTS_SGM_MNG_DELETE_RESULTS_TABLE_RESULT,
  OTS_SGM_MNG_DELETE_RESULTS_TABLE_SEGMENT_ID,
  OTS_SGM_MNG_DELETE_RESULTS_TITLE,
} from '../../../../content';
import * as Styled from './index.styles';
import type { SetTtdOtsBuyableSegmentsPayload } from '../../../../graphql/hooks/useSetTtdOtsBuyableSegments';

/** Generic confirm dialog (reusable for any bulk / destructive action). */
export type BulkActionConfirmModalProps = {
  open: boolean;
  loading: boolean;
  title: React.ReactNode;
  /** Optional content below the title (e.g. delete confirmation line). */
  headerSummary?: React.ReactNode;
  body: React.ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  /** Optional id for aria-labelledby */
  titleId?: string;
  bodyId?: string;
};

export function BulkActionConfirmModal({
  open,
  loading,
  title,
  headerSummary,
  body,
  cancelLabel,
  confirmLabel,
  onClose,
  onConfirm,
  titleId = 'bulk-action-confirm-title',
  bodyId = 'bulk-action-confirm-body',
}: BulkActionConfirmModalProps) {
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!loading) onClose();
      }}
      maxWidth="sm"
      fullWidth
      headerID={titleId}
      header={
        <Styled.ModalHeaderColumn>
          <Styled.ModalCardTitle id={titleId}>
            {headerSummary}
          </Styled.ModalCardTitle>
        </Styled.ModalHeaderColumn>
      }
      bodyID={bodyId}
      body={<Box>{body}</Box>}
      footer={
        <Styled.ModalFooterRow>
          <Button
            color="primary"
            variant="outlined"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onConfirm}
            disabled={loading}
            style={{
              backgroundColor: '#e53935',
              color: '#fff',
              fontWeight: 600,
              minWidth: loading ? 120 : undefined,
            }}
          >
            {loading ? (
              <CircularProgress color="inherit" size={22} />
            ) : (
              confirmLabel
            )}
          </Button>
        </Styled.ModalFooterRow>
      }
    />
  );
}

export type BulkDeleteResultsModalProps = {
  open: boolean;
  payload: SetTtdOtsBuyableSegmentsPayload | null;
  /** @deprecated Kept for callers; per-segment outcomes are shown in the results table from `payload.results`. */
  failedRows?: { segmentId: string; segmentName: string; detailUrl: string }[];
  onClose: () => void;
  /** Override default results title (i18n / reuse). */
  title?: React.ReactNode;
  closeLabel?: string;
};

function resultsSummaryText(payload: SetTtdOtsBuyableSegmentsPayload): string {
  const { succeeded, failed } = payload.summary;
  if (failed === 0) return OTS_SGM_MNG_DELETE_RESULTS_SUMMARY_ALL_OK;
  if (succeeded === 0) return OTS_SGM_MNG_DELETE_RESULTS_SUMMARY_NONE;
  return OTS_SGM_MNG_DELETE_RESULTS_SUMMARY_PARTIAL;
}

function resultLabel(status: string): string {
  return status === 'SUCCESS'
    ? OTS_SGM_MNG_DELETE_RESULTS_STATUS_SUCCESS
    : OTS_SGM_MNG_DELETE_RESULTS_STATUS_FAILED;
}

/**
 * Presents summary (requested / succeeded / failed) and a per-segment table (segmentId, result, error.message).
 */
export function BulkDeleteResultsModal({
  open,
  payload,
  onClose,
  title = OTS_SGM_MNG_DELETE_RESULTS_TITLE,
  closeLabel = OTS_SGM_MNG_DELETE_RESULTS_CLOSE,
}: BulkDeleteResultsModalProps) {
  if (!open || !payload) return null;

  const results = Array.isArray(payload.results) ? payload.results : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      bodyID="bulk-delete-results-body"
      headerID="bulk-delete-results-title"
      header={
        <Styled.ModalCardTitle id="bulk-delete-results-title">
          {title}
        </Styled.ModalCardTitle>
      }
      body={
        <Box>
          <Styled.DeleteResultsSummaryGrid>
            <div>
              <Styled.DeleteResultsLabel>
                {OTS_SGM_MNG_DELETE_RESULTS_REQUESTED}
              </Styled.DeleteResultsLabel>
              <Styled.DeleteResultsValue>
                {payload.summary.requested}
              </Styled.DeleteResultsValue>
            </div>
            <div>
              <Styled.DeleteResultsLabel>
                {OTS_SGM_MNG_DELETE_RESULTS_SUCCEEDED}
              </Styled.DeleteResultsLabel>
              <Styled.DeleteResultsValue>
                {payload.summary.succeeded}
              </Styled.DeleteResultsValue>
            </div>
            <div>
              <Styled.DeleteResultsLabel>
                {OTS_SGM_MNG_DELETE_RESULTS_FAILED}
              </Styled.DeleteResultsLabel>
              <Styled.DeleteResultsValue>
                {payload.summary.failed}
              </Styled.DeleteResultsValue>
            </div>
          </Styled.DeleteResultsSummaryGrid>
          <Styled.DeleteResultsFinalSummary>
            {resultsSummaryText(payload)}
          </Styled.DeleteResultsFinalSummary>
          {results.length > 0 ? (
            <>
              <Styled.DeleteResultsTableWrap>
                <Styled.DeleteResultsTable>
                  <thead>
                    <tr>
                      <Styled.DeleteResultsTh>
                        {OTS_SGM_MNG_DELETE_RESULTS_TABLE_SEGMENT_ID}
                      </Styled.DeleteResultsTh>
                      <Styled.DeleteResultsTh style={{ width: 110 }}>
                        {OTS_SGM_MNG_DELETE_RESULTS_TABLE_RESULT}
                      </Styled.DeleteResultsTh>
                      <Styled.DeleteResultsTh>
                        {OTS_SGM_MNG_DELETE_RESULTS_TABLE_ERROR}
                      </Styled.DeleteResultsTh>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row) => {
                      const ok = row.status === 'SUCCESS';
                      const errMsg =
                        row.error?.message && String(row.error.message).trim()
                          ? String(row.error.message).trim()
                          : OTS_SGM_MNG_DELETE_RESULTS_ERROR_EMPTY;
                      return (
                        <tr key={row.segmentId}>
                          <Styled.DeleteResultsTdMono>
                            {row.segmentId}
                          </Styled.DeleteResultsTdMono>
                          <Styled.DeleteResultsTd>
                            {ok ? (
                              <Styled.DeleteResultsStatusSuccess>
                                {resultLabel(row.status)}
                              </Styled.DeleteResultsStatusSuccess>
                            ) : (
                              <Styled.DeleteResultsStatusFailed>
                                {resultLabel(row.status)}
                              </Styled.DeleteResultsStatusFailed>
                            )}
                          </Styled.DeleteResultsTd>
                          <Styled.DeleteResultsTd>
                            {errMsg}
                          </Styled.DeleteResultsTd>
                        </tr>
                      );
                    })}
                  </tbody>
                </Styled.DeleteResultsTable>
              </Styled.DeleteResultsTableWrap>
            </>
          ) : null}
        </Box>
      }
      footer={
        <Styled.ModalFooterRow>
          <Button color="primary" variant="contained" onClick={onClose}>
            {closeLabel}
          </Button>
        </Styled.ModalFooterRow>
      }
    />
  );
}
